/**
 * Texto da home (`app/app/index.tsx`) e da entidade da marca (título e
 * descrição do site, Organization, WebSite, rodapé).
 *
 * Fica fora da tela para três leitores usarem o mesmo texto: a página, o
 * JSON-LD (o FAQPage sai do mesmo array do FAQ visível) e o gerador da versão
 * em markdown. Todo número vem de `../facts`; nada de preço, limite ou taxa
 * escrito à mão. Todo texto passa pelo portão `post-controlapag`: o que é dos
 * planos pagos diz que é, e só aparece fluxo que existe no app.
 */
import {
  COMPANY,
  CUSTOM_PLAN,
  FREE_PLAN,
  PAYMENT_FEES,
  PLATFORMS,
  PROSE,
  REMINDERS,
  SOLO_PLANS,
  TEAM_PLAN,
  formatBRL,
  type SoloPlanFact,
} from '../facts';

const cheapest = formatBRL(Math.min(...SOLO_PLANS.map((plan) => plan.webPrice)));
const free = FREE_PLAN.maxClientsPerMonth;
const [daysA, daysB] = REMINDERS.chargeDaysBeforeOptions;

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const joinPt = (items: readonly string[]) =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`;

// ---------------------------------------------------------------------------
// Entidade da marca
// ---------------------------------------------------------------------------

/** `<title>` padrão do site e da home (até 60 caracteres). */
export const BRAND_TITLE = 'Controlapag: agenda e cobrança no WhatsApp para autônomos';

/** Descrição única: meta da home, Organization e WebSite (120 a 160 caracteres). */
export const BRAND_DESCRIPTION = `App para prestador de serviço organizar clientes, agenda e cobranças num lugar só. Grátis para até ${free} clientes por mês; planos a partir de ${cheapest}.`;

/** Frase da marca: `slogan` da Organization e texto do rodapé. */
export const BRAND_SLOGAN = 'A forma mais fácil de manter tudo em dia.';

// ---------------------------------------------------------------------------
// Topo
// ---------------------------------------------------------------------------

export const HOME_HERO = {
  eyebrow: 'Cobrança e agenda que funcionam sozinhas',
  h1: 'App de agenda e cobrança no WhatsApp para quem presta serviço',
  /** Bloco de resposta logo depois do H1: afirmativo, com número, sem link. */
  answer: `A Controlapag é um app brasileiro para autônomo organizar clientes, agenda e cobranças. Grátis para até ${free} clientes por mês; lembrete no WhatsApp e link de pagamento a partir de ${cheapest}.`,
  /** Mesmo bloco sem preço, para o app do iPhone (diretriz 3.1.1 da App Store). */
  answerNoPrice: `A Controlapag é um app brasileiro para autônomo organizar clientes, agenda e cobranças. Grátis para até ${free} clientes por mês; lembrete no WhatsApp e link de pagamento nos planos pagos.`,
} as const;

export const TRUST_BADGES = [
  { icon: 'people-outline', label: `Conta grátis com até ${free} clientes por mês` },
  { icon: 'shield-checkmark-outline', label: 'Dados protegidos (LGPD)' },
  { icon: 'checkmark-done-outline', label: 'Cancele quando quiser' },
] as const;

// ---------------------------------------------------------------------------
// Chat de exemplo: só mensagens que a plataforma manda de verdade
// (`api/src/database/migrations/1773200000000-SeedFixedWhatsappTemplates.ts`).
// ---------------------------------------------------------------------------

export interface ChatMessage {
  text: string;
  outgoing: boolean;
  time: string;
}

export const REMINDER_CHAT_CONTACT = 'Beatriz Rocha';

export const REMINDER_CHAT: ChatMessage[] = [
  {
    text: 'Olá, Beatriz!\n\nPassando para lembrar da cobrança do serviço "Faxina semanal".\nValor: R$ 150,00\nVencimento: 10/10 (amanhã)\n\nPara pagar agora, acesse o link:\ncontrolapag.com.br/checkout/…',
    outgoing: true,
    time: '09:00',
  },
  {
    text: 'Olá, Beatriz!\n\nRecebemos o pagamento da cobrança do serviço "Faxina semanal". Obrigado!\n\nValor: R$ 150,00',
    outgoing: true,
    time: '10:42',
  },
];

export const WHATSAPP_SECTION = {
  badge: 'Veja como funciona no WhatsApp',
  heading: 'Como o lembrete chega no WhatsApp do cliente?',
  capsule: `Nos planos pagos, a Controlapag manda o lembrete pelo número dela, em nome do seu negócio, ${daysA} ou ${daysB} dias antes e no dia do vencimento.`,
  body: 'Você não precisa conectar nada. A mensagem leva o valor, o vencimento e o link para pagar. Quando o pagamento entra, o cliente recebe o aviso de pagamento recebido.',
} as const;

export const QUOTE = {
  lead: 'Enquanto o lembrete sai sozinho no WhatsApp,',
  highlight: 'você fica livre para atender o próximo cliente.',
} as const;

// ---------------------------------------------------------------------------
// O que a Controlapag faz
// ---------------------------------------------------------------------------

export interface HomeFeature {
  icon: string;
  title: string;
  description: string;
  link?: { label: string; href: string };
}

export const FEATURES_SECTION = {
  heading: 'O que a Controlapag faz?',
  capsule: `A Controlapag junta agenda, clientes, cobranças e despesas num app só, ${PROSE.platforms}. O que é dos planos pagos está marcado.`,
} as const;

export const FEATURES: HomeFeature[] = [
  {
    icon: 'calendar-outline',
    title: 'Agenda e link de agendamento',
    description:
      'Você organiza os atendimentos na agenda. Com o perfil público (planos pagos), o cliente marca sozinho nos horários que você libera.',
  },
  {
    icon: 'logo-whatsapp',
    title: 'Lembrete no WhatsApp',
    description: `Nos planos pagos, o lembrete de cobrança sai ${daysA} ou ${daysB} dias antes e no dia do vencimento. Quando o pagamento entra, o cliente recebe o aviso de pagamento recebido.`,
  },
  {
    icon: 'document-text-outline',
    title: 'Contratos e mensalidades',
    description:
      'O contrato fica na conta do cliente, que ele acessa com login próprio. Contrato com cobrança avulsa é grátis; mensalidade e contrato recorrente estão nos planos pagos.',
  },
  {
    icon: 'receipt-outline',
    title: 'Despesas e lucro do mês',
    description: 'Registre gastos por categoria e veja o lucro real do mês, não só o que entrou.',
  },
  {
    icon: 'card-outline',
    title: 'Pagamento pelo link',
    description:
      'Nos planos pagos, o cliente paga pelo link com Pix, cartão ou boleto e a cobrança dá baixa sozinha, com taxa por pagamento. Ou ele paga por fora e você confirma no app.',
    link: { label: 'Ver taxas e prazos', href: '/ajuda/recebimentos' },
  },
  {
    icon: 'bar-chart-outline',
    title: 'Painel financeiro',
    description: 'O recebido no mês, o que falta receber e quem está atrasado, num painel só.',
  },
];

// ---------------------------------------------------------------------------
// Painel (seção "beneficios")
// ---------------------------------------------------------------------------

export const PANEL_SECTION = {
  heading: 'Como saber quem pagou e quem falta pagar?',
  capsule:
    'No painel da Controlapag, cada cobrança aparece como paga, pendente ou atrasada, e o topo mostra o recebido no mês e o que falta receber.',
} as const;

export const BENEFITS = [
  'O lembrete sai sozinho no dia que você escolher (planos pagos)',
  'Pagou por fora? Toque em Confirmar recebimento e confirme',
  'Modelo de contrato reaproveitado em cada cliente',
  'Despesas no app: você vê quanto sobrou no mês',
  `No plano Equipe, de ${TEAM_PLAN.minProfessionals} a ${TEAM_PLAN.maxProfessionals} profissionais na mesma conta`,
  'Menos tempo cobrando, mais tempo atendendo',
];

// ---------------------------------------------------------------------------
// Como começar
// ---------------------------------------------------------------------------

export const HOW_IT_WORKS_SECTION = {
  heading: 'Como começar na Controlapag?',
  capsule:
    'Você cria a conta grátis, o assistente monta serviços, valores e agenda pelo seu ramo, e você cadastra os clientes.',
} as const;

export const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Crie a conta e responda o assistente',
    description: 'O assistente de configuração monta serviços, valores, agenda e forma de receber pelo seu ramo.',
  },
  {
    step: '02',
    title: 'Cadastre seus clientes',
    description: 'Cada cliente entra com login próprio e vê as cobranças, os contratos e os horários dele.',
  },
  {
    step: '03',
    title: 'Acompanhe pelo painel',
    description:
      'Você vê quem pagou e quem falta pagar. Nos planos pagos, o lembrete e o aviso de pagamento recebido saem sozinhos no WhatsApp.',
  },
];

// ---------------------------------------------------------------------------
// Ramos e guias
// ---------------------------------------------------------------------------

export const SEGMENTS_SECTION = {
  badge: 'Feita para o seu ramo',
  heading: 'Para quem é a Controlapag?',
  capsule:
    'Para autônomo e pequeno negócio de serviço que atende com hora marcada ou mensalidade. Veja como fica no seu ramo:',
} as const;

export const GUIDES_SECTION = {
  heading: 'Guias mais lidos',
  capsule: 'Respostas para o dia a dia de quem cobra cliente: falta, atraso, lembrete e preço.',
  allLabel: 'Ver todos os guias',
} as const;

/** Guias da home, em ordem. Slug que sumir de `GUIDE_PAGES` some daqui também. */
export const HOME_GUIDE_SLUGS = [
  'cliente-faltou-sem-avisar',
  'como-cobrar-mensalidade-atrasada',
  'mensagem-lembrete-de-horario',
  'quanto-cobrar-por-hora',
];

// ---------------------------------------------------------------------------
// Planos
// ---------------------------------------------------------------------------

export interface HomePlan {
  id: string;
  name: string;
  description: string;
  priceMonthly: number;
  capacity?: string;
  features: string[];
  highlight?: boolean;
}

export const PLANS_SECTION = {
  heading: 'Quanto custa a Controlapag?',
  capsule: `A conta grátis vai até ${free} clientes por mês. Os planos pagos custam ${PROSE.fromPrice} no site, e você cancela quando quiser.`,
  tableCaption: 'Comparação da conta grátis com os planos Prático, Pro e Gestão',
} as const;

/** Card da conta grátis (não existe no Stripe: é a conta sem plano). */
export const FREE_PLAN_CARD: HomePlan = {
  id: 'free',
  name: 'Grátis',
  description: 'Para organizar a agenda e as cobranças avulsas sem pagar nada.',
  priceMonthly: 0,
  capacity: `Até ${free} clientes/mês`,
  features: FREE_PLAN.includes.map(capitalize),
};

/**
 * Texto dos planos pagos: espelha `description` e `features` de
 * `api/src/stripe/constants/plans.ts` (o teste `seo-home.test.mjs` compara).
 * Preço e limite vêm de `SOLO_PLANS`.
 */
const PLAN_TEXT: Record<SoloPlanFact['id'], { description: string; features: string[]; highlight?: boolean }> = {
  basic: {
    description: 'Para quem está começando: lembrete no WhatsApp, perfil público e mensalidades.',
    features: [
      'Lembrete automático no WhatsApp',
      'Perfil público com agenda',
      'Mensalidades e contratos',
      'Até 10 fotos na galeria',
    ],
  },
  professional: {
    description: 'Para quem vive do próprio negócio e tem a agenda cheia.',
    features: [
      'Tudo do Prático',
      'Até 200 clientes por mês',
      'Destaque na busca de profissionais',
      'Até 30 fotos na galeria',
    ],
    highlight: true,
  },
  enterprise: {
    description: 'Para quem atende sozinho com volume alto: turmas, aulas em grupo e agenda cheia.',
    features: ['Tudo do Pro', 'Até 500 clientes por mês', 'Destaque na busca', 'Até 100 fotos na galeria'],
  },
};

/** Reserva dos planos pagos: sai no HTML estático e fica até `/stripe/plans` responder. */
export const FALLBACK_PLANS: HomePlan[] = SOLO_PLANS.map((plan) => ({
  id: plan.id,
  name: plan.name,
  priceMonthly: plan.webPrice,
  capacity: `Até ${plan.maxClientsPerMonth} clientes/mês`,
  ...PLAN_TEXT[plan.id],
}));

const yes = 'Sim';
const no = 'Não';

/** Tabela Grátis × Prático × Pro × Gestão. `prices` deixa a tela usar o preço vivo. */
export function planComparisonTable(prices: Partial<Record<string, number>> = {}) {
  const price = (plan: SoloPlanFact) => `${formatBRL(prices[plan.id] ?? plan.webPrice)} por mês`;
  const paid = (value: string) => SOLO_PLANS.map(() => value);
  return {
    columns: [
      { label: 'Recurso' },
      { label: 'Grátis' },
      ...SOLO_PLANS.map((plan) => ({ label: plan.name, highlight: plan.id === 'professional' })),
    ],
    rows: [
      ['Preço no site', formatBRL(0), ...SOLO_PLANS.map(price)],
      ['Clientes por mês', String(free), ...SOLO_PLANS.map((plan) => String(plan.maxClientsPerMonth))],
      ['Agenda, clientes e cobrança avulsa', yes, ...paid(yes)],
      ['Painel, despesas e lucro do mês', yes, ...paid(yes)],
      ['Lembrete automático no WhatsApp', no, ...paid(yes)],
      ['Perfil público com agenda', no, ...paid(yes)],
      ['Mensalidade e contrato recorrente', 'Não (só avulsa)', ...paid(yes)],
      ['Recebimento pelo link', no, ...paid('Sim, com taxa por pagamento')],
      ['Fotos na galeria', 'Sem perfil público', ...SOLO_PLANS.map((plan) => `Até ${plan.maxPortfolioPhotos}`)],
      ['Destaque na busca', no, ...SOLO_PLANS.map((plan) => (plan.featuredInDiscovery ? yes : no))],
    ],
  };
}

export const CUSTOM_PLAN_BLOCK = {
  heading: `Atende mais de ${CUSTOM_PLAN.aboveClientsPerMonth} clientes por mês sozinho, ou tem mais de ${CUSTOM_PLAN.aboveProfessionals} profissionais?`,
  body: 'Fazemos um plano sob medida, com migração de dados assistida e gerente de conta dedicado.',
} as const;

// ---------------------------------------------------------------------------
// Chamada final
// ---------------------------------------------------------------------------

export const FINAL_CTA = {
  heading: 'Pare de cobrar na mão',
  body: `Crie sua conta grátis e conheça. Quando quiser o lembrete no WhatsApp e o pagamento pelo link, escolha um plano ${PROSE.fromPrice}.`,
  bodyNoPrice:
    'Crie sua conta grátis e conheça. Quando quiser o lembrete no WhatsApp e o pagamento pelo link, escolha um plano pago.',
  sticky: 'Comece com a conta grátis',
} as const;

// ---------------------------------------------------------------------------
// Perguntas frequentes (FAQ visível e FAQPage saem daqui)
// ---------------------------------------------------------------------------

export interface HomeFaqItem {
  question: string;
  answer: string;
  /** Link mostrado depois da resposta (fica fora do JSON-LD). */
  link?: { label: string; href: string };
  /** Cita preço de assinatura: o app do iPhone não mostra (diretriz 3.1.1). */
  hasPrice?: boolean;
}

const [practical, pro, gestao] = SOLO_PLANS;

export const HOME_FAQ: HomeFaqItem[] = [
  {
    question: 'O que tem na conta grátis?',
    answer: `A conta grátis vai até ${free} clientes por mês e tem ${joinPt(FREE_PLAN.includes)}. Lembrete automático no WhatsApp, perfil público com agenda, mensalidade e recebimento pelo link estão nos planos pagos.`,
  },
  {
    question: 'Quanto custa a Controlapag?',
    answer: `A conta grátis não custa nada, para até ${free} clientes por mês. No site, o ${practical.name} custa ${formatBRL(practical.webPrice)} por mês (até ${practical.maxClientsPerMonth} clientes), o ${pro.name} ${formatBRL(pro.webPrice)} (até ${pro.maxClientsPerMonth}) e o ${gestao.name} ${formatBRL(gestao.webPrice)} (até ${gestao.maxClientsPerMonth}). O plano Equipe começa em ${formatBRL(TEAM_PLAN.fromPrice)} por mês com ${TEAM_PLAN.minProfessionals} profissionais. O recebimento pelo link tem taxa por pagamento: ${PROSE.pixFee}, ${PROSE.cardFee} e ${PROSE.boletoFee}.`,
    link: { label: 'Ver taxas e prazos do recebimento', href: '/ajuda/recebimentos' },
    hasPrice: true,
  },
  {
    question: 'Como funciona a cobrança pelo WhatsApp?',
    answer: `Nos planos pagos, a Controlapag manda o lembrete de cobrança no WhatsApp do seu cliente ${daysA} ou ${daysB} dias antes do vencimento (ou nos dias que você escolher) e no dia do vencimento, com o valor, a data e o link para pagar. Quando o pagamento entra, o cliente recebe o aviso de pagamento recebido. Na conta grátis, você cria a cobrança avulsa e acompanha no painel, sem o envio automático.`,
  },
  {
    question: 'Preciso instalar algo no WhatsApp?',
    answer:
      'Não. O lembrete sai pelo número da Controlapag, em nome do seu negócio. Você só cadastra o número do cliente. O envio automático está nos planos pagos.',
  },
  {
    question: 'Como funcionam os contratos?',
    answer:
      'Você monta um modelo uma vez e reaproveita em cada cliente. O contrato fica na conta do cliente, que ele acessa com login próprio. Contrato com cobrança avulsa é grátis; mensalidade e contrato com cobrança recorrente estão nos planos pagos.',
  },
  {
    question: 'Dá para ter mais de um profissional na conta?',
    answer: `Sim. No plano Equipe, contratado pelo site, você convida de ${TEAM_PLAN.minProfessionals} a ${TEAM_PLAN.maxProfessionals} profissionais para a mesma conta, que dividem a agenda e a carteira de clientes. Você escolhe quantos profissionais e quantos clientes por mês, e o preço sai na hora.`,
  },
  {
    question: 'Quais formas de pagamento meus clientes podem usar?',
    answer: `Por padrão, o cliente paga direto a você, do jeito que combinarem (Pix, transferência, dinheiro ou maquininha), e você toca em Confirmar recebimento e confirma. Nos planos pagos, ele também pode pagar pelo link com Pix, cartão (${joinPt(PAYMENT_FEES.cardBrands)}, à vista) ou boleto, se você ligar, e a cobrança dá baixa sozinha. O link tem taxa por pagamento, e a Controlapag não fica com parte dele.`,
    link: { label: 'Ver taxas e prazos do recebimento', href: '/ajuda/recebimentos' },
  },
  {
    question: 'Posso cancelar quando quiser?',
    answer:
      'Sim, não há fidelidade. Quem assinou pelo site cancela ou troca de plano no próprio app. Quem assinou pelo iPhone cancela nos Ajustes do iPhone, em Assinaturas da App Store.',
  },
  {
    question: 'E se a minha operação for maior que os planos?',
    answer: `O plano Sob Medida é para quem atende mais de ${CUSTOM_PLAN.aboveClientsPerMonth} clientes por mês sozinho ou tem mais de ${CUSTOM_PLAN.aboveProfessionals} profissionais, com migração de dados assistida e gerente de conta dedicado. Fale com a equipe pelo e-mail ${COMPANY.email}.`,
  },
];

export const FAQ_SECTION = {
  heading: 'Perguntas frequentes',
  capsule: 'O que mais perguntam antes de criar a conta.',
} as const;

// ---------------------------------------------------------------------------
// JSON-LD
// ---------------------------------------------------------------------------

export const faqJsonLd = (items: HomeFaqItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  })),
});

const operatingSystem = ['Web', PLATFORMS.ios && 'iOS', PLATFORMS.android && 'Android']
  .filter(Boolean)
  .join(', ');

/** SoftwareApplication da home, com um `Offer` por plano (o grátis com preço 0). */
export function softwareApplicationJsonLd(siteUrl: string) {
  const offer = (name: string, price: number, description: string) => ({
    '@type': 'Offer',
    name,
    price: price.toFixed(2),
    priceCurrency: 'BRL',
    description,
    url: `${siteUrl}/#planos`,
  });
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': `${siteUrl}/#software`,
    name: COMPANY.name,
    applicationCategory: 'BusinessApplication',
    operatingSystem,
    downloadUrl: [PLATFORMS.appStoreUrl, PLATFORMS.playStoreUrl],
    url: siteUrl,
    description: BRAND_DESCRIPTION,
    inLanguage: 'pt-BR',
    publisher: { '@id': `${siteUrl}/#organization` },
    offers: [
      offer('Grátis', 0, `Até ${free} clientes por mês, com cobranças avulsas.`),
      ...SOLO_PLANS.map((plan) =>
        offer(plan.name, plan.webPrice, `Até ${plan.maxClientsPerMonth} clientes por mês, preço mensal no site.`),
      ),
    ],
  };
}
